'use client';

import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';

// Stores the raw eligibility reason CODE from the API (e.g. "30230").
// The locale-specific display text is resolved at the point of consumption
// using the current locale's AEM page data, so switching language always
// shows the correctly translated message.
const ELIGIBLE_REASON_STORAGE_KEY = 'eligibleReasonCode';

interface EligibleReasonContextType {
  eligibleReason: string | null;
  setEligibleReason: (eligibleReason: string) => void;
  clearEligibleReason: () => void;
}

const EligibleReasonContext = createContext<EligibleReasonContextType | undefined>(undefined);

export function EligibleReasonProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [eligibleReason, setEligibleReason] = useState<string | null>(() => {
    if (globalThis.window === undefined) {
      return null;
    }

    return sessionStorage.getItem(ELIGIBLE_REASON_STORAGE_KEY);
  });

  const handleSetEligibleReason = useCallback((reason: string) => {
    setEligibleReason(reason);
    sessionStorage.setItem(ELIGIBLE_REASON_STORAGE_KEY, reason);
  }, []);

  const clearEligibleReason = useCallback(() => {
    setEligibleReason(null);
    sessionStorage.removeItem(ELIGIBLE_REASON_STORAGE_KEY);
  }, []);

  const value = useMemo(
    () => ({ eligibleReason, setEligibleReason: handleSetEligibleReason, clearEligibleReason }),
    [eligibleReason, handleSetEligibleReason, clearEligibleReason]
  );

  return (
    <EligibleReasonContext.Provider value={value}>
      {children}
    </EligibleReasonContext.Provider>
  );
}

export function useEligibleReason() {
  const context = useContext(EligibleReasonContext);
  if (context === undefined) {
    throw new Error('useEligibleReason must be used within an EligibleReasonProvider');
  }
  return context;
}
